export const toolboxSolutions = {
  "AI/ML Governance & MLOps": {
    "Model Risk Register & Drift Watch": {
      description:
        "I want to create a consistent, living inventory of all production models and a simple way to detect, summarize, and act on drift or performance issues across teams.",
      solution: {
        plan: {
          solution_title: "Model Risk Register & Drift Watch",
          solution_description:
            "This solution establishes a unified model register, standardizes metadata capture, and auto-summarizes monitoring signals into decision-ready briefs. Teams will be able to see ownership, KPIs, risks, and drift insights in one place and publish periodic governance packets for fast review.",
          solution_context:
            "Centralizing model facts and telemetry reduces blind spots and speeds responses to degradation. GENAI helps normalize disparate inputs, generate concise executive insights from raw metrics, and enforce consistent governance language—so committees can reason over risk without wading through dashboards or ad hoc docs.",
          steps: [
            {
              step_title: "Create Project Workspace",
              step_description:
                "Set up a centralized workspace to hold all artifacts, references, and outputs for the Model Risk Register & Drift Watch initiative.",
              toolbox_type: "PROJECT",
              toolbox_name: "Model Risk Register Workspace",
              toolbox_description:
                "Centralized project for organizing model inventory, drift insights, briefs, and governance reports.",
            },
            {
              step_title: "Standardize Model Intake",
              step_description:
                "Collect model metadata, owners, KPIs, data sources, and intended use.",
              toolbox_type: "FORM",
              toolbox_name: "Model Intake Form",
              toolbox_description:
                "Conditional sections for PII, fairness, and monitoring hooks.",
            },
            {
              step_title: "Generate Model Fact Sheets",
              step_description:
                "Create concise profiles for each model with risk ratings and control mappings.",
              toolbox_type: "HANDOUT",
              toolbox_name: "Model Fact Sheet Builder",
              toolbox_description:
                "Branded 1-page briefs for committees and partners.",
            },
            {
              step_title: "Summarize Drift & Performance Signals",
              step_description:
                "Aggregate monitoring signals into executive-ready snapshots.",
              toolbox_type: "KEY_INSIGHTS",
              toolbox_name: "Drift Insight Summarizer",
              toolbox_description:
                "Traffic-light summaries with recommended actions.",
            },
            {
              step_title: "Publish Quarterly Governance Report",
              step_description:
                "Roll up the register and trends into a governance packet.",
              toolbox_type: "RESEARCH_REPORT",
              toolbox_name: "Model Governance Quarterly",
              toolbox_description:
                "Includes risk posture, incidents, mitigations, and roadmap.",
            },
            {
              step_title: "Route for Committee Review & Sign-off",
              step_description:
                "Share packet for approvals, capture comments, and freeze version.",
              toolbox_type: "None",
              toolbox_name: "",
              toolbox_description: "",
            },
          ],
        },
      },
    },
    "Responsible AI Policy → Controls Mapping Kit": {
      description:
        "I want to translate our high-level Responsible AI principles into concrete, testable controls, checklists, and validation templates that delivery teams can actually use.",
      solution: {
        plan: {
          solution_title: "RAI Policy to Controls Mapping",
          solution_description:
            "This solution converts policy text into measurable control statements, lifecycle checklists, and validation templates, then packages an internal implementation guide. It gives practitioners step-by-step artifacts for adoption and reviewers a consistent evidence trail for compliance.",
          solution_context:
            "Policies often fail at the last mile. By using GENAI to extract control intents, propose evidence types, and draft standardized templates, teams get operational clarity while maintaining fidelity to policy. The mapping also enables repeatable reasoning during reviews by aligning controls, tests, and sign-offs.",
          steps: [
            {
              step_title: "Create Project Workspace",
              step_description:
                "Set up a centralized workspace to hold all artifacts, references, and outputs for the RAI Policy to Controls Mapping effort.",
              toolbox_type: "PROJECT",
              toolbox_name: "RAI Controls Mapping Workspace",
              toolbox_description:
                "Centralized project for policy sources, control mappings, checklists, validation templates, and guides.",
            },
            {
              step_title: "Extract Control Statements",
              step_description:
                "Parse policy docs into measurable, testable controls.",
              toolbox_type: "KEY_INSIGHTS",
              toolbox_name: "Policy Insight Extractor",
              toolbox_description:
                "Identifies control objectives and evidence types.",
            },
            {
              step_title: "Author Control Checklists",
              step_description: "Create checklists tied to lifecycle stages.",
              toolbox_type: "HANDOUT",
              toolbox_name: "RAI Control Checklists",
              toolbox_description:
                "Stage-gated lists for data, modeling, deployment.",
            },
            {
              step_title: "Create Validation Templates",
              step_description:
                "Provide templates for fairness, robustness, and explainability tests.",
              toolbox_type: "MODEL_VALIDATION",
              toolbox_name: "RAI Validation Templates",
              toolbox_description:
                "Prebuilt sections for metrics, thresholds, and evidence.",
            },
            {
              step_title: "Compile Implementation Guide",
              step_description:
                "Publish an internal guide explaining how to apply controls.",
              toolbox_type: "RESEARCH_REPORT",
              toolbox_name: "RAI Implementation Guide",
              toolbox_description:
                "Includes examples, pitfalls, and evidence samples.",
            },
            {
              step_title: "Stakeholder Walkthrough & Q&A",
              step_description:
                "Host a short review session; log feedback and update v1.0.",
              toolbox_type: "None",
              toolbox_name: "",
              toolbox_description: "",
            },
          ],
        },
      },
    },
    "Feature Store & Data Lineage Governance Pack": {
      description:
        "I want to document feature definitions, provenance, and usage guidelines so teams can reuse features safely and avoid quality or privacy issues.",
      solution: {
        plan: {
          solution_title: "Feature Governance Pack",
          solution_description:
            "This solution captures feature metadata via structured forms, generates reusable feature cards, and produces portfolio health insights alongside a governance whitepaper. Stakeholders receive concise guidance on fit-for-use, caveats, and ownership—reducing rework and risk.",
          solution_context:
            "Features are assets when meaning and lineage are clear. GENAI assists by harmonizing descriptions, inferring potential risks from transformations and sources, and summarizing portfolio-level signals (freshness, drift, duplication). That synthesized view supports faster, safer decisions in MLOps pipelines.",
          steps: [
            {
              step_title: "Create Project Workspace",
              step_description:
                "Set up a centralized workspace to hold all artifacts, references, and outputs for the Feature Governance Pack.",
              toolbox_type: "PROJECT",
              toolbox_name: "Feature Governance Workspace",
              toolbox_description:
                "Centralized project for feature metadata, lineage docs, cards, portfolio insights, and governance standards.",
            },
            {
              step_title: "Capture Feature Metadata",
              step_description:
                "Collect owners, transformations, data sources, and privacy notes.",
              toolbox_type: "FORM",
              toolbox_name: "Feature Metadata Form",
              toolbox_description:
                "Supports SQL/ETL snippets and lineage links.",
            },
            {
              step_title: "Create Feature Cards",
              step_description: "Generate concise, reusable docs per feature.",
              toolbox_type: "HANDOUT",
              toolbox_name: "Feature Card Generator",
              toolbox_description:
                "Use-case, caveats, versioning, quality metrics.",
            },
            {
              step_title: "Summarize Portfolio Health",
              step_description:
                "Produce periodic insights on freshness, quality, and usage.",
              toolbox_type: "KEY_INSIGHTS",
              toolbox_name: "Feature Portfolio Insights",
              toolbox_description: "Highlights stale features and remediation.",
            },
            {
              step_title: "Author Governance Whitepaper",
              step_description:
                "Document standards for creation, review, and retirement.",
              toolbox_type: "RESEARCH_REPORT",
              toolbox_name: "Feature Governance Whitepaper",
              toolbox_description:
                "Policies for naming, lineage, and approvals.",
            },
            {
              step_title: "Publish to Data Catalog & Notify Owners",
              step_description:
                "Sync final docs/cards to catalog and message owners.",
              toolbox_type: "None",
              toolbox_name: "",
              toolbox_description: "",
            },
          ],
        },
      },
    },
  },

  "Regulatory Reporting": {
    "AML/SAR Narrative QA & Evidence Pack": {
      description:
        "I want to standardize how SAR narratives are drafted, quality-checked, and packaged with evidence so audits and exams are smooth and consistent.",
      solution: {
        plan: {
          solution_title: "SAR Narrative QA & Evidence Pack",
          solution_description:
            "This solution introduces structured evidence intake, a narrative QA checklist, insight summaries for examiners, and a compiled audit packet. It ensures completeness, clarity, and traceability from alert to filing, decreasing revision cycles and findings.",
          solution_context:
            "SAR quality hinges on disciplined inputs and clear storytelling. GENAI helps extract key facts from evidence, flag narrative gaps against QA criteria, and generate examiner-ready synopses—supporting transparent regulatory reasoning and easing the burden on investigators and compliance reviewers.",
          steps: [
            {
              step_title: "Create Project Workspace",
              step_description:
                "Set up a centralized workspace to hold all artifacts, references, and outputs for the SAR Narrative QA & Evidence Pack.",
              toolbox_type: "PROJECT",
              toolbox_name: "SAR QA & Evidence Workspace",
              toolbox_description:
                "Centralized project for evidence intake forms, QA checklists, examiner summaries, and audit packets.",
            },
            {
              step_title: "Collect Case Evidence",
              step_description:
                "Gather alerts, investigations, and supporting docs.",
              toolbox_type: "FORM",
              toolbox_name: "SAR Evidence Intake",
              toolbox_description:
                "Validates required fields for completeness.",
            },
            {
              step_title: "Create QA Checklist",
              step_description:
                "Generate narrative quality checks and compliance cues.",
              toolbox_type: "HANDOUT",
              toolbox_name: "SAR QA Checklist",
              toolbox_description:
                "Clarity, chronology, materiality, and redactions.",
            },
            {
              step_title: "Summarize Examiner-Ready Insights",
              step_description:
                "Prepare an executive summary of the case and narrative strength.",
              toolbox_type: "KEY_INSIGHTS",
              toolbox_name: "Examiner Summary Builder",
              toolbox_description: "Highlights risk factors and rationale.",
            },
            {
              step_title: "Compile Audit Packet",
              step_description:
                "Assemble a structured, versioned report for review.",
              toolbox_type: "RESEARCH_REPORT",
              toolbox_name: "Audit Packet Generator",
              toolbox_description:
                "Covers evidence index, QA outcomes, sign-offs.",
            },
            {
              step_title: "Pre-Exam Dry Run",
              step_description: "Run an internal mock review and address gaps.",
              toolbox_type: "None",
              toolbox_name: "",
              toolbox_description: "",
            },
          ],
        },
      },
    },
  },

  "Audit Readiness": {
    "Model Change Management Dossier (SR 11-7 aligned)": {
      description:
        "I want to capture every material model change—why we changed it, what we tested, and how it affects risk—in a regulator-ready package.",
      solution: {
        plan: {
          solution_title: "Model Change Management Dossier",
          solution_description:
            "This solution adds a structured change request form, auto-generates a validation addendum for targeted testing, and produces both an examiner brief and a full change dossier. The result is a consistent, auditable trail from rationale to approval.",
          solution_context:
            "Regulators expect explainability and continuity. GENAI accelerates consistency by templating impact narratives, aligning tests to affected risks, and summarizing outcomes in clear language. The dossier makes it easy for stakeholders to reason about residual risk and control sufficiency.",
          steps: [
            {
              step_title: "Create Project Workspace",
              step_description:
                "Set up a centralized workspace to hold all artifacts, references, and outputs for the Model Change Management Dossier.",
              toolbox_type: "PROJECT",
              toolbox_name: "Model Change Dossier Workspace",
              toolbox_description:
                "Centralized project for change requests, validation addenda, examiner briefs, and compiled dossiers.",
            },
            {
              step_title: "Change Request Capture",
              step_description:
                "Collect rationale, data/code diffs, and impacted controls.",
              toolbox_type: "FORM",
              toolbox_name: "Model Change Request",
              toolbox_description: "Supports attachments and reviewer routing.",
            },
            {
              step_title: "Generate Validation Addendum",
              step_description: "Create targeted tests and results summary.",
              toolbox_type: "MODEL_VALIDATION",
              toolbox_name: "Validation Addendum Builder",
              toolbox_description: "Bias, stability, and backtesting deltas.",
            },
            {
              step_title: "Produce Examiner Brief",
              step_description: "Summarize what changed, why, and outcomes.",
              toolbox_type: "HANDOUT",
              toolbox_name: "Examiner Brief Designer",
              toolbox_description: "1–2 page highlights with sign-off table.",
            },
            {
              step_title: "Compile Change Dossier",
              step_description:
                "Create a full report with lineage and approvals.",
              toolbox_type: "RESEARCH_REPORT",
              toolbox_name: "Change Management Dossier",
              toolbox_description:
                "Chronology, control mapping, evidence index.",
            },
            {
              step_title: "Governance Board Review",
              step_description:
                "Present the dossier for approval; log conditions and actions.",
              toolbox_type: "None",
              toolbox_name: "",
              toolbox_description: "",
            },
          ],
        },
      },
    },
    "Fair Lending Monitoring & Redress Report": {
      description:
        "Run periodic fairness tests, interpret results, and publish regulator-ready reports with clear remediation and customer communication materials.",
      solution: {
        plan: {
          solution_title: "Fair Lending Monitoring & Redress",
          solution_description:
            "This solution structures input capture for fairness analyses, synthesizes findings into prioritized insights, and compiles a comprehensive report plus plain-language customer handouts. Leaders get an executive readout to approve mitigation and redress plans.",
          solution_context:
            "Fair-lending oversight requires rigorous analytics and accessible explanation. GENAI supports both by converting statistical output into decision narratives, highlighting drivers and effect sizes, and drafting audience-appropriate communications—bridging quantitative evidence and policy action.",
          steps: [
            {
              step_title: "Create Project Workspace",
              step_description:
                "Set up a centralized workspace to hold all artifacts, references, and outputs for the Fair Lending Monitoring & Redress program.",
              toolbox_type: "PROJECT",
              toolbox_name: "Fair Lending Monitoring Workspace",
              toolbox_description:
                "Centralized project for test inputs, findings summaries, regulator reports, and customer communication materials.",
            },
            {
              step_title: "Collect Test Inputs",
              step_description:
                "Capture cohorts, metrics, thresholds, and time windows.",
              toolbox_type: "FORM",
              toolbox_name: "Fairness Test Intake",
              toolbox_description:
                "Templates for impact ratio and counterfactual tests.",
            },
            {
              step_title: "Generate Findings Summary",
              step_description:
                "Summarize disparities, significance, and drivers.",
              toolbox_type: "KEY_INSIGHTS",
              toolbox_name: "Fairness Insight Summarizer",
              toolbox_description: "Prioritizes actions by lift and exposure.",
            },
            {
              step_title: "Create Regulator Report",
              step_description: "Produce a comprehensive monitoring report.",
              toolbox_type: "RESEARCH_REPORT",
              toolbox_name: "Fair Lending Report",
              toolbox_description:
                "Methods, results, mitigations, redress plan.",
            },
            {
              step_title: "Prepare Customer Communication Handout",
              step_description:
                "Draft clear, accessible notices explaining outcomes and remedies.",
              toolbox_type: "HANDOUT",
              toolbox_name: "Customer Redress Brief",
              toolbox_description: "Plain-language, brand-ready content.",
            },
            {
              step_title: "Executive Readout & Go/No-Go",
              step_description:
                "Present findings to leadership and confirm remediation steps.",
              toolbox_type: "None",
              toolbox_name: "",
              toolbox_description: "",
            },
          ],
        },
      },
    },
  },

  "Learning & Enablement": {
    "GenAI for Investigators: Micro-Cert": {
      description:
        "Create a short, hands-on certification that teaches investigators to use GENAI for case triage, evidence synthesis, and high-quality SAR drafting.",
      solution: {
        plan: {
          solution_title: "GenAI for Investigators Micro-Cert",
          solution_description:
            "This solution designs a compact course with scenario-based labs and printable pocket guides. Learners practice with realistic data, receive immediate feedback, and finish with a capstone that demonstrates proficiency.",
          solution_context:
            "Skill adoption improves when practice mirrors real work. GENAI-powered labs simulate drafting and QA, while automated insight summaries reinforce key patterns. The micro-cert scaffolds reasoning—from prompt design to evidence-backed narratives—so performance gains persist after training.",
          steps: [
            {
              step_title: "Create Project Workspace",
              step_description:
                "Set up a centralized workspace to hold all artifacts, references, and outputs for the GenAI for Investigators Micro-Cert.",
              toolbox_type: "PROJECT",
              toolbox_name: "Investigators Micro-Cert Workspace",
              toolbox_description:
                "Centralized project for course modules, labs, pocket guides, assessments, and cohort materials.",
            },
            {
              step_title: "Design Course Outline",
              step_description:
                "Define 4 modules (Triage, Evidence, Drafting, QA).",
              toolbox_type: "COURSE",
              toolbox_name: "Course Builder",
              toolbox_description:
                "Auto-generate objectives, slides, and assessments.",
            },
            {
              step_title: "Build SAR Drafting Lab",
              step_description:
                "Interactive practice with prompts and redaction tasks.",
              toolbox_type: "CONCEPT_LABS",
              toolbox_name: "SAR Drafting Lab",
              toolbox_description:
                "Streamlit-based, scenario-driven exercises.",
            },
            {
              step_title: "Create Pocket Guides",
              step_description: "Produce printable quick-reference sheets.",
              toolbox_type: "HANDOUT",
              toolbox_name: "Investigator Pocket Guides",
              toolbox_description: "Prompts, do/don’t lists, QA checks.",
            },
            {
              step_title: "Cohort Kickoff & Orientation",
              step_description:
                "Host a 30-minute kickoff; assign lab access and expectations.",
              toolbox_type: "None",
              toolbox_name: "",
              toolbox_description: "",
            },
          ],
        },
      },
    },
    "MLOps Onboarding Sprint (7-Day)": {
      description:
        "I want to stand up a one-week onboarding sprint that gets new MLOps engineers productive with our stack, governance, and operational playbooks.",
      solution: {
        plan: {
          solution_title: "MLOps Onboarding Sprint",
          solution_description:
            "This solution delivers a sequenced curriculum, daily hands-on labs, and bite-sized insight recaps, concluding with a capstone demo and badge. Graduates can ship models with CI/CD, monitoring, and rollback confidence.",
          solution_context:
            "High-velocity learning benefits from tight feedback loops. GENAI assists by generating tailored explanations, step hints, and daily summaries from lab outputs—compressing time-to-competence and aligning engineers with governance from day one.",
          steps: [
            {
              step_title: "Create Project Workspace",
              step_description:
                "Set up a centralized workspace to hold all artifacts, references, and outputs for the MLOps Onboarding Sprint.",
              toolbox_type: "PROJECT",
              toolbox_name: "MLOps Onboarding Sprint Workspace",
              toolbox_description:
                "Centralized project for daily modules, operational labs, recap insights, and capstone deliverables.",
            },
            {
              step_title: "Author Daily Modules",
              step_description:
                "Containerization, CI/CD for models, monitoring, rollback.",
              toolbox_type: "COURSE",
              toolbox_name: "Sprint Course Builder",
              toolbox_description: "Schedules content and quizzes per day.",
            },
            {
              step_title: "Create Operational Labs",
              step_description: "Break/fix, drift alerts, hotfix rollbacks.",
              toolbox_type: "CONCEPT_LABS",
              toolbox_name: "Ops Lab Pack",
              toolbox_description: "Notebook + app labs with scoring.",
            },
            {
              step_title: "Summarize Key Takeaways",
              step_description: "Daily insight recaps to reinforce learning.",
              toolbox_type: "KEY_INSIGHTS",
              toolbox_name: "Daily Insight Summaries",
              toolbox_description: "Auto-emails or LMS cards.",
            },
            {
              step_title: "Final Readout & Badge Award",
              step_description:
                "Run capstone demo and award completion badges.",
              toolbox_type: "None",
              toolbox_name: "",
              toolbox_description: "",
            },
          ],
        },
      },
    },
    "Product Manager AI Primer": {
      description:
        "I want to create a practical program for PMs to scope AI features, assess data needs and risks, and coordinate cross-functionally to ship responsibly.",
      solution: {
        plan: {
          solution_title: "AI Primer for Product Managers",
          solution_description:
            "This solution organizes focused modules, mini case studies, a hands-on prompting lab, and readiness handouts. PMs leave with checklists, templates, and a shared vocabulary for partnering with engineering and risk.",
          solution_context:
            "PM success in AI hinges on framing, measurable outcomes, and ethical tradeoffs. GENAI supports exploration of solution spaces, reframing of requirements, and rapid drafting of artifacts (PRDs, comms, checklists), enabling clearer reasoning and faster stakeholder alignment.",
          steps: [
            {
              step_title: "Create Project Workspace",
              step_description:
                "Set up a centralized workspace to hold all artifacts, references, and outputs for the AI Primer for Product Managers.",
              toolbox_type: "PROJECT",
              toolbox_name: "PM AI Primer Workspace",
              toolbox_description:
                "Centralized project for PM course modules, mini-cases, prompting labs, and readiness handouts.",
            },
            {
              step_title: "Create Course Modules",
              step_description:
                "Problem framing, data needs, success metrics, ship/readiness.",
              toolbox_type: "COURSE",
              toolbox_name: "PM Course Builder",
              toolbox_description: "Objectives, slides, speaker notes.",
            },
            {
              step_title: "Author Mini Case Studies",
              step_description: "Realistic tradeoffs and ethical dilemmas.",
              toolbox_type: "CASE_STUDY",
              toolbox_name: "PM Case Builder",
              toolbox_description: "Harvard-style format with teaching notes.",
            },
            {
              step_title: "Hands-on Prompting Lab",
              step_description:
                "Design, evaluate, and iterate on prompts for workflows.",
              toolbox_type: "CONCEPT_LABS",
              toolbox_name: "Prompt Engineering Lab",
              toolbox_description: "Scenario-based exercises with rubrics.",
            },
            {
              step_title: "Stakeholder Showcase",
              step_description:
                "Demo capstone artifacts to leadership and collect feedback.",
              toolbox_type: "None",
              toolbox_name: "",
              toolbox_description: "",
            },
            {
              step_title: "Create PM Readiness Handouts",
              step_description:
                "Ship-checklists and stakeholder comms templates.",
              toolbox_type: "HANDOUT",
              toolbox_name: "PM Readiness Pack",
              toolbox_description:
                "Go/no-go checklist, risk notes, comms copy.",
            },
          ],
        },
      },
    },
  },

  "Knowledge Management": {
    "Executive Briefing Hub": {
      description:
        "I want to turn sprawling reports and updates into crisp weekly executive briefs that highlight decisions, risks, and owners.",
      solution: {
        plan: {
          solution_title: "Executive Briefing Hub",
          solution_description:
            "This solution ingests source documents, extracts key insights, and publishes a branded one-pager plus a newsletter digest. Leadership gets consistent signal over noise and a clear action register.",
          solution_context:
            "Executives need synthesis, not volume. GENAI excels at condensing heterogeneous inputs into role-aware narratives and surfacing deltas week over week. This supports faster reasoning about priorities while preserving links back to source evidence.",
          steps: [
            {
              step_title: "Create Project Workspace",
              step_description:
                "Set up a centralized workspace to hold all artifacts, references, and outputs for the Executive Briefing Hub.",
              toolbox_type: "PROJECT",
              toolbox_name: "Executive Briefing Hub Workspace",
              toolbox_description:
                "Centralized project for source ingestion, weekly briefs, newsletter digests, and action tracking.",
            },
            {
              step_title: "Ingest Source Docs",
              step_description:
                "Pull strategy memos, vendor reports, and internal notes.",
              toolbox_type: "KEY_INSIGHTS",
              toolbox_name: "Insight Extractor",
              toolbox_description: "Highlights themes, risks, actions.",
            },
            {
              step_title: "Generate Weekly Brief",
              step_description:
                "Produce a concise, branded summary with top 5 takeaways.",
              toolbox_type: "HANDOUT",
              toolbox_name: "Executive Brief Designer",
              toolbox_description: "One-pager with callouts and charts.",
            },
            {
              step_title: "Publish Digest",
              step_description:
                "Share a newsletter-style summary to stakeholders.",
              toolbox_type: "NEWS_LETTER",
              toolbox_name: "Exec Digest",
              toolbox_description: "Email-ready digest with links to briefs.",
            },
            {
              step_title: "Leadership Standup Review",
              step_description:
                "Walk through key calls-to-action and assign owners.",
              toolbox_type: "None",
              toolbox_name: "",
              toolbox_description: "",
            },
          ],
        },
      },
    },
    "Research-to-Blog Pipeline": {
      description:
        "I want to convert internal research into engaging blog posts and companion visuals so insights travel beyond the researcher’s desk.",
      solution: {
        plan: {
          solution_title: "Research-to-Blog Pipeline",
          solution_description:
            "This solution distills findings, drafts a polished blog article, and creates a shareable handout. It streamlines editorial flow from deep analysis to accessible storytelling with clear CTAs.",
          solution_context:
            "Great ideas spread when packaged well. GENAI bridges researcher language and audience expectations by extracting claims, mapping evidence, and proposing structures that resonate—helping readers reason about implications without losing rigor.",
          steps: [
            {
              step_title: "Create Project Workspace",
              step_description:
                "Set up a centralized workspace to hold all artifacts, references, and outputs for the Research-to-Blog Pipeline.",
              toolbox_type: "PROJECT",
              toolbox_name: "Research-to-Blog Workspace",
              toolbox_description:
                "Centralized project for findings synthesis, blog drafts, summary handouts, and editorial workflow.",
            },
            {
              step_title: "Synthesize Key Findings",
              step_description: "Extract claims, evidence, and implications.",
              toolbox_type: "KEY_INSIGHTS",
              toolbox_name: "Finding Synthesizer",
              toolbox_description: "Ranked insights with confidence notes.",
            },
            {
              step_title: "Draft Blog Article",
              step_description: "Create a polished, SEO-aware post.",
              toolbox_type: "BLOG",
              toolbox_name: "Blog Draft Builder",
              toolbox_description:
                "Outline, headline variants, CTA suggestions.",
            },
            {
              step_title: "Design Shareable Summary",
              step_description:
                "Produce a visual companion or slide-style handout.",
              toolbox_type: "HANDOUT",
              toolbox_name: "Summary Handout Designer",
              toolbox_description: "Diagrams, key stats, and pull quotes.",
            },
            {
              step_title: "Editorial Review & Publication",
              step_description:
                "Finalize copy, approvals, and schedule release.",
              toolbox_type: "None",
              toolbox_name: "",
              toolbox_description: "",
            },
          ],
        },
      },
    },
    "Policy & SOP Summarization Vault": {
      description:
        "I want to maintain a living vault of concise policy and SOP summaries, tailored by role, with clear change notices and an authoritative reference.",
      solution: {
        plan: {
          solution_title: "Policy & SOP Summarization Vault",
          solution_description:
            "This solution generates role-based summaries, produces quick-reference handouts, sends update newsletters, and compiles a cited compendium. Teams see what changed, what to do, and where to find the full source.",
          solution_context:
            "Adoption rises when guidance is targeted and current. GENAI automates extraction of actionable steps, highlights diffs across versions, and adapts tone to each audience—supporting better day-to-day reasoning and compliance with minimal reading burden.",
          steps: [
            {
              step_title: "Create Project Workspace",
              step_description:
                "Set up a centralized workspace to hold all artifacts, references, and outputs for the Policy & SOP Summarization Vault.",
              toolbox_type: "PROJECT",
              toolbox_name: "Policy & SOP Vault Workspace",
              toolbox_description:
                "Centralized project for role-based summaries, handouts, change newsletters, and the cited compendium.",
            },
            {
              step_title: "Summarize by Role",
              step_description:
                "Produce tailored SOP/policy summaries per audience.",
              toolbox_type: "KEY_INSIGHTS",
              toolbox_name: "Role-Based Summaries",
              toolbox_description:
                "Pulls ‘what changed’ and ‘what to do’ per role.",
            },
            {
              step_title: "Publish Role Handouts",
              step_description: "Create quick-reference one-pagers.",
              toolbox_type: "HANDOUT",
              toolbox_name: "Role Handout Generator",
              toolbox_description: "Action checklists and escalation paths.",
            },
            {
              step_title: "Announce Updates",
              step_description: "Send digest of recent SOP/policy changes.",
              toolbox_type: "NEWS_LETTER",
              toolbox_name: "Change Notice Newsletter",
              toolbox_description: "Highlights diffs and effective dates.",
            },
            {
              step_title: "Quarterly Policy Council Review",
              step_description:
                "Meet to confirm adoption, capture exceptions, and set next updates.",
              toolbox_type: "None",
              toolbox_name: "",
              toolbox_description: "",
            },
            {
              step_title: "Archive Full Reference",
              step_description: "Maintain a structured, searchable repository.",
              toolbox_type: "RESEARCH_REPORT",
              toolbox_name: "SOP & Policy Compendium",
              toolbox_description:
                "Source excerpts with citations and versioning.",
            },
          ],
        },
      },
    },
  },
};
